<template>
  <DpaNewsBaseModal
    ref="modal"
    :submit-button-label="t('savedSearches.saveSearch')"
    :submit-disabled="shouldDisableInput"
    :is-loading="isFetching"
    tracking-place="renameSaveSearch"
    @submit="handleRenameSaveSearch">
    <template #header>
      {{ t("savedSearches.renameModalTitle") }}
    </template>
    <UiInput
      v-model="name"
      aria-required="true"
      :input-status="inputValidation"
      :error-message="errorMessage"
      :label="t('generic.name')"
      @keyup.enter="handleRenameSaveSearch" />
    <UiLabel
      for="colorPicker"
      class="mb-3 inline-block text-xs text-neutral-subtle">
      {{ t("control.chooseColor") }}
    </UiLabel>
    <UiColorPicker
      id="colorPicker"
      v-model="color"
      @update:model-value="
        trackEvent(
          'renameSavedSearch:modal_click_colorPicker_colorPicker',
          $event,
        )
      " />
  </DpaNewsBaseModal>
</template>

<script setup lang="ts">
import UiColorPicker from "@dpa-id-components/dpa-shared-components/src/components/UiColorPicker/UiColorPicker.vue";
import UiInput from "@dpa-id-components/dpa-shared-components/src/components/UiInput/UiInput.vue";
import UiLabel from "@dpa-id-components/dpa-shared-components/src/components/UiLabel/UiLabel.vue";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import DpaNewsBaseModal from "@/components/shared/DpaNewsBaseModal/DpaNewsBaseModal.vue";
import { useRenameSearch } from "@/composition/api/savedSearches.ts";
import { useInputValidation } from "@/composition/shared/useInputValidation.ts";
import { useTracking } from "@/composition/shared/useTracking.ts";
import type { SavedSearchItem } from "@/types/savedSearch";
import { handleLocalError } from "@/utils/errorHandling.ts";

const { t } = useI18n();
const { trackEvent } = useTracking();

const { searchColor, searchName, searchId } = defineProps<{
  searchName: string;
  searchId: string;
  searchColor: string;
}>();

const modal = useTemplateRef<InstanceType<typeof DpaNewsBaseModal>>("modal");

const name = ref(searchName);
const color = ref(searchColor);

const { inputValidation, errorMessage, shouldDisableInput } =
  useInputValidation({
    inputValue: name,
    characterLimit: 100,
  });

const emit = defineEmits<{
  edited: [];
}>();

const saveSearchItem = computed<Partial<SavedSearchItem>>(() => ({
  name: name.value,
  color: color.value,
}));

const {
  execute: renameSavedSearch,
  error: renameSaveSearchError,
  isFetching,
} = useRenameSearch(searchId, saveSearchItem);

const handleRenameSaveSearch = async () => {
  if (shouldDisableInput.value) {
    return;
  }
  await renameSavedSearch();

  if (renameSaveSearchError.value) {
    handleLocalError(
      renameSaveSearchError.value.status,
      t("errors.saveSearch.failed"),
    );
    return;
  }
  emit("edited");
  modal.value?.requestClose();
};

function showModal() {
  name.value = searchName;
  color.value = searchColor;
  modal.value?.showModal();
}

function requestClose() {
  modal.value?.requestClose();
}

function close() {
  modal.value?.close();
}

defineExpose({ showModal, close, requestClose });
</script>
